import { createFileRoute } from "@tanstack/react-router";
import { getOrgIdWithFallback } from "@/lib/utils";
import { DataTable } from "@/components/inventory/data-table";
import { MainTitle } from "@/components/main-title";
import {
  DEFAULT_PRODUCT_SORT,
  productsQueryOpts,
  type InventoryFilters,
  type ProductSort,
} from "@/hooks/product";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/providers/auth";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { isGated } from "@/hooks/gated";
import GatedOverlay from "@/components/ui/gated-overlay";
import { useOrg } from "@/providers/organization";
import { platformsQueryOpts } from "@/hooks/platform";
import { columns } from "@/components/inventory/table-helper";

export const Route = createFileRoute("/$org/inventory")({
  loader: async ({ context }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );

    const platforms = await context.queryClient.ensureQueryData(
      platformsQueryOpts({
        setToken,
        orgId: orgId,
        token: getToken() as string,
      }),
    );

    context.queryClient.ensureQueryData(
      productsQueryOpts(
        {
          token: getToken() ?? "",
          setToken,
          orgId: orgId,
        },
        {
          sync: "all",
          pcIds:
            platforms.map((platform) => platform.platform_connection_id) ?? [],
          collectionIds: [],
        },
        1,
        30,
        "",
      ),
    );

    // We'll load the data in the component instead since we need search params
    return { gated: gated?.is_gated, platforms };
  },
  component: InventoryComponent,
});

function InventoryComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { user, getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const { data: platforms } = useQuery(
    platformsQueryOpts({
      setToken,
      token: getToken() as string,
      orgId: orgId,
    }),
  );

  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 30 });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<InventoryFilters>({
    sync: "all",
    pcIds: platforms?.map((platform) => platform.platform_connection_id) ?? [],
    collectionIds: [],
  });
  const [sort, setSort] = useState<ProductSort>(DEFAULT_PRODUCT_SORT);
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((currentPagination) =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 },
    );
  }, []);

  const handleFiltersChange = useCallback((value: InventoryFilters) => {
    setFilters(value);
    setPagination((currentPagination) =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 },
    );
  }, []);

  const handleSortChange = useCallback((value: ProductSort) => {
    setSort(value);
    setPagination((currentPagination) =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 },
    );
  }, []);

  const { data: productsResponse } = useQuery({
    ...productsQueryOpts(
      {
        token: getToken() ?? "",
        setToken,
        orgId: resolvedOrgId,
      },
      filters,
      pagination.pageIndex + 1,
      pagination.pageSize,
      searchTerm,
      sort,
    ),
    placeholderData: (previousData) => previousData,
  });

  const products = productsResponse?.products || [];

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div className="flex flex-col">
      <MainTitle title={t("nav.inventory")} />

      <div className="rounded flex-1">
        <DataTable
          columns={columns}
          data={products}
          filters={filters}
          org={user.account?.orgs[0].name ?? ""}
          pagination={pagination}
          pageCount={productsResponse?.pagination.totalPages ?? 1}
          totalCount={
            productsResponse?.pagination.totalCount ?? products.length
          }
          onPaginationChange={setPagination}
          searchValue={searchTerm}
          onSearchChange={handleSearchChange}
          onFiltersChange={handleFiltersChange}
          sort={sort}
          onSortChange={handleSortChange}
        />
      </div>
    </div>
  );
}
